import React from 'react';
import { Editor } from '@tiptap/react';
import { Button } from '@os/components/ui/button';
import { Separator } from '@os/components/ui/separator';
import { Bold, Italic, Underline, Strikethrough, Code, List, ListOrdered, Quote, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Image as ImageIcon, Rows, Eraser, Undo2, Redo2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@os/components/ui/select';
import './MapleWord.styles.css';
import InsertTableGrid from "./InsertTableGrid";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

type Props = { editor: Editor | null };

const levelLabel = (level: number) => `H${level}`;

const fontFamilies = [
  { label: 'Default', value: 'default' },
  { label: 'Inter', value: 'Inter, system-ui, sans-serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { label: 'JetBrains Mono', value: '"JetBrains Mono", monospace' },
];

const fontSizes = [
  { label: '12', value: '12px' },
  { label: '14', value: '14px' },
  { label: '16', value: '16px' },
  { label: '18', value: '18px' },
  { label: '20', value: '20px' },
  { label: '24', value: '24px' },
  { label: '28', value: '28px' },
];

const MapleWordMenuBar: React.FC<Props> = ({ editor }) => {
  if (!editor) return null;

  const setHeading = (level: 1 | 2 | 3 | 4 | 5 | 6) => editor.chain().focus().toggleHeading({ level }).run();
  const isHeading = (level: number) => editor.isActive('heading', { level });

  const setParagraph = () => editor.chain().focus().setParagraph().run();
  const isParagraph = () => editor.isActive('paragraph');

  const toggleLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter URL', previousUrl || '');
    if (url === null) return; // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleFontFamily = (value: string) => {
    if (!editor) return;
    if (value === 'default') {
      editor.chain().focus().unsetFontFamily().run();
      return;
    }
    editor.chain().focus().setFontFamily(value).run();
  };

  const handleFontSize = (value: string) => {
    editor.chain().focus().setFontSize(value).run();
  };

  const handleTextColor = (value: string) => {
    editor.chain().focus().setColor(value).run();
  };

  const handleBackgroundColor = (value: string) => {
    editor.chain().focus().setHighlight({ color: value }).run();
  };

  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const pickImage = () => imageInputRef.current?.click();
  const onImagePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : '';
      if (src) {
        editor.chain().focus().setImage({ src }).run();
      }
    };
    reader.readAsDataURL(file);
    // reset input
    e.target.value = '';
  };

  // Control Insert Table popover visibility
  const [tablePopoverOpen, setTablePopoverOpen] = React.useState(false);

  // Clear formatting handler (restored to ribbon)
  const clearFormatting = () => {
    editor
      .chain()
      .focus()
      .unsetAllMarks()
      .unsetTextAlign()
      .setParagraph()
      .run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border rounded-md bg-muted/40">
      <div className="flex items-center gap-1">
        <Button variant={isParagraph() ? 'secondary' : 'outline'} size="sm" onMouseDown={(e) => { e.preventDefault(); setParagraph(); }}>
          P
        </Button>
        {[1,2,3].map((lvl) => (
          <Button
            key={lvl}
            variant={isHeading(lvl) ? 'secondary' : 'outline'}
            size="sm"
            onMouseDown={(e) => { e.preventDefault(); setHeading(lvl as 1|2|3); }}
          >
            {levelLabel(lvl)}
          </Button>
        ))}
      </div>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <div className="flex items-center gap-1">
        <Button variant={editor.isActive('bold') ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}>
          <Bold className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive('italic') ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}>
          <Italic className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive('underline') ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}>
          <Underline className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive('strike') ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleStrike().run(); }}>
          <Strikethrough className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive('code') ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleCode().run(); }}>
          <Code className="h-4 w-4" />
        </Button>
      </div>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <div className="flex items-center gap-1">
        <Button variant={editor.isActive({ textAlign: 'left' }) ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('left').run(); }}>
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive({ textAlign: 'center' }) ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('center').run(); }}>
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive({ textAlign: 'right' }) ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('right').run(); }}>
          <AlignRight className="h-4 w-4" />
        </Button>
      </div>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <div className="flex items-center gap-1">
        <Button variant={editor.isActive('bulletList') ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}>
          <List className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive('orderedList') ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}>
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive('blockquote') ? 'secondary' : 'outline'} size="icon" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBlockquote().run(); }}>
          <Quote className="h-4 w-4" />
        </Button>
        <Button variant={editor.isActive('link') ? 'secondary' : 'outline'} size="sm" onMouseDown={(e) => { e.preventDefault(); toggleLink(); }}>
          <LinkIcon className="h-4 w-4 mr-1" /> Link
        </Button>
      </div>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <div className="flex items-center gap-2">
        <Select onValueChange={handleFontFamily}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Font family" />
          </SelectTrigger>
          <SelectContent className="z-[10000]">
            {fontFamilies.map(f => (
              <SelectItem key={f.label} value={f.value}>
                <span style={{ fontFamily: f.value || undefined }}>{f.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select onValueChange={handleFontSize}>
          <SelectTrigger className="w-24">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent className="z-[10000]">
            {fontSizes.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Text</label>
          <input
            type="color"
            className="color-input w-8 h-8 p-0 rounded cursor-pointer border-0 outline-none"
            onChange={(e) => handleTextColor(e.target.value)}
            title="Text color"
          />
          <label className="text-xs text-muted-foreground">Highlight</label>
          <input
            type="color"
            className="color-input w-8 h-8 p-0 rounded cursor-pointer border-0 outline-none"
            onChange={(e) => handleBackgroundColor(e.target.value)}
            title="Background color"
          />
        </div>

        <Button variant="outline" size="icon" onMouseDown={(e) => { e.preventDefault(); pickImage(); }} title="Insert image">
          <ImageIcon className="h-4 w-4" />
        </Button>
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={onImagePicked} />
      </div>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Insert Table grid popover */}
      <Popover open={tablePopoverOpen} onOpenChange={setTablePopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" title="Insert table">
            <Rows className="h-4 w-4 mr-1" />
            Table
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[1000000] w-auto p-3">
          <InsertTableGrid
            onSelect={(rows, cols) => {
              const inserted = editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();

              if (!inserted) {
                // Fallback: directly insert a table node (3x3 default with header row)
                const headerRow = {
                  type: 'tableRow',
                  content: Array.from({ length: cols }).map(() => ({
                    type: 'tableHeader',
                    content: [{ type: 'paragraph' }]
                  }))
                };
                const bodyRows = Array.from({ length: Math.max(rows - 1, 0) }).map(() => ({
                  type: 'tableRow',
                  content: Array.from({ length: cols }).map(() => ({
                    type: 'tableCell',
                    content: [{ type: 'paragraph' }]
                  }))
                }));

                editor
                  .chain()
                  .focus()
                  .insertContent({ type: 'table', content: [headerRow, ...bodyRows] } as any)
                  .run();
              }

              setTablePopoverOpen(false);
            }}
            defaultRows={3}
            defaultCols={3}
          />
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Clear / Undo / Redo restored to ribbon */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onMouseDown={(e) => { e.preventDefault(); clearFormatting(); }}
          title="Clear formatting"
        >
          <Eraser className="h-4 w-4 mr-1" />
          Clear
        </Button>
        <Button
          variant="outline"
          size="icon"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().undo().run(); }}
          title="Undo"
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().redo().run(); }}
          title="Redo"
        >
          <Redo2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Note: All specific table action buttons were removed from the ribbon, now available via right-click in-table */}
    </div>
  );
};

export default MapleWordMenuBar;